/**
 * Cookie consent model.
 *
 * Ported from the Academy's `marketing-consent.ts` (bhfield-academy/frontend),
 * minus its e-commerce half — this site has no cart, no purchase events and no
 * authenticated surfaces carrying pricing, so `trackCommerce`, the checkout
 * redaction and the public-path allow-list all came out. What is left is the
 * part that matters here: nothing optional runs until someone says yes.
 *
 * Client-safe on purpose. The provider and the footer both import from it, and
 * pulling in a `.server` module would fail the build.
 */

export const CONSENT_COOKIE = 'bhf_cookie_consent';

/** Six months. Long enough not to nag, short enough that a choice is revisited. */
export const CONSENT_MAX_AGE = 60 * 60 * 24 * 180;

export type CookieConsent = {
  analytics: boolean;
  advertising: boolean;
};

export const DENIED_CONSENT: CookieConsent = { analytics: false, advertising: false };
export const GRANTED_CONSENT: CookieConsent = { analytics: true, advertising: true };

/**
 * Global Privacy Control is a browser-level request not to sell or share data,
 * or use it for targeted advertising. It says nothing about first-party
 * analytics, so analytics stays the visitor's choice while advertising becomes a
 * hard opt-out we cannot override.
 */
export function applyGlobalPrivacyControl(consent: CookieConsent, globalPrivacyControl: boolean): CookieConsent {
  return globalPrivacyControl ? { ...consent, advertising: false } : consent;
}

export function hasGlobalPrivacyControl(value: Navigator | undefined): boolean {
  return Boolean(value && (value as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true);
}

/**
 * Reads the decision out of a Cookie header. Returns null for "no decision yet",
 * which is what makes the banner appear — anything malformed or from an older
 * shape is treated the same way rather than guessed at.
 */
export function parseConsentCookie(cookieHeader: string | null | undefined): CookieConsent | null {
  if (!cookieHeader) return null;
  const raw = cookieHeader
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CONSENT_COOKIE}=`))
    ?.slice(CONSENT_COOKIE.length + 1);
  if (!raw) return null;

  try {
    const value = JSON.parse(decodeURIComponent(raw)) as unknown;
    if (
      typeof value === 'object' &&
      value !== null &&
      typeof (value as CookieConsent).analytics === 'boolean' &&
      typeof (value as CookieConsent).advertising === 'boolean'
    ) {
      return {
        analytics: (value as CookieConsent).analytics,
        advertising: (value as CookieConsent).advertising,
      };
    }
  } catch {
    // Stale or hand-edited value: treat as no decision.
  }
  return null;
}

export function serializeConsentCookie(consent: CookieConsent, secure = true): string {
  return [
    `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(consent))}`,
    'Path=/',
    `Max-Age=${CONSENT_MAX_AGE}`,
    'SameSite=Lax',
    ...(secure ? ['Secure'] : []),
  ].join('; ');
}

/** Google Consent Mode v2 signal names. */
export function toGoogleConsent(consent: CookieConsent) {
  return {
    analytics_storage: consent.analytics ? ('granted' as const) : ('denied' as const),
    ad_storage: consent.advertising ? ('granted' as const) : ('denied' as const),
    ad_user_data: consent.advertising ? ('granted' as const) : ('denied' as const),
    ad_personalization: consent.advertising ? ('granted' as const) : ('denied' as const),
  };
}

export function hasOptionalConsent(consent: CookieConsent | null): boolean {
  return Boolean(consent?.analytics || consent?.advertising);
}

/**
 * Surfaces that never set an optional cookie, so they never ask about one:
 * the admin console, the invitation-only review study, and the health check.
 * Kept in step with `UNTRACKED_PREFIXES` in `root.tsx`, which decides the same
 * thing for the tag manager itself.
 */
const UNASKED_PREFIXES = ['/admin', '/review', '/healthcheck'];

export function isConsentablePath(pathname: string): boolean {
  return !UNASKED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}
